import { JSX } from "react"
import { ErrorDisplay } from "../error/ErrorDisplay"
import Pagination from "../Pagination"
import { CustomLoadingSpinner } from "../spinners/LoadingSpinner"

export interface TableCell {
    content: string | JSX.Element
    onClick?: () => void
}

export interface TableRow {
    cells: TableCell[]
    onClick?: () => void
}

export interface TablePagination {
    page_number: number
    page_size: number
    on_page_change: (page_number: number) => void
    on_page_size_change?: (page_size: number) => void;
}

interface SimpleTableProps {
    header_row: TableRow
    data_rows: TableRow[]
    empty_table_message?: string
    pagination?: TablePagination
    loading?: boolean
    error?: boolean
    onRetry?: () => void
}

const SimpleTable: React.FC<SimpleTableProps> = ({
    header_row,
    data_rows,
    empty_table_message,
    pagination,
    loading,
    error,
    onRetry
}) => {
    return (
        <>
            <div className="bg-white border border-gray-200 rounded-md shadow-sm overflow-hidden">
                <div className="overflow-auto">
                    <table className="min-w-max w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                {header_row.cells.map((cell, index) => (
                                    <th 
                                        key={index}
                                        scope="col" 
                                        className="px-2 py-2 text-left text-xs font-semibold text-[#374151] whitespace-nowrap"
                                        onClick={cell.onClick}
                                    >
                                        {cell.content}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                        {loading ? (
                            <tr>
                            <td colSpan={header_row.cells.length} className="px-6 py-4">
                            <CustomLoadingSpinner 
                                title="Fetching cases..." 
                                subtitle="Please wait while we load the case list." 
                                minHeight="min-h-[200px]" 
                            />
                        </td>
                        </tr>
                        )  : error ? (
                                <tr>
                                    <td  colSpan={header_row.cells.length} className="px-6 py-4 text-center">
                                        <ErrorDisplay showRetryButton={onRetry !== undefined} onRetry={onRetry}/>
                                    </td>
                                </tr>
                            ) : data_rows.length === 0 && empty_table_message ? (
                                <tr>
                                    <td colSpan={header_row.cells.length} className="px-6 py-4 text-center text-sm text-gray-500">
                                        {empty_table_message}
                                    </td>
                                </tr>
                            ) : (data_rows.map((row, index) => (
                                    <tr key={index} className="hover:bg-gray-50 cursor-pointer" onClick={row.onClick}>
                                        {row.cells.map((cell, index) => (
                                            <td 
                                                key={index} 
                                                className="px-2 py-4 whitespace-nowrap text-sm text-[#0A0A0A] font-normal align-middle"
                                                onClick={cell.onClick}
                                            >
                                                {cell.content}
                                            </td>
                                        ))}
                                    </tr>
                                )))
                            }
                        </tbody>
                    </table>
                </div>
            </div>
            {pagination && (
                <Pagination
                    pageNumber={pagination.page_number}
                    pageSize={pagination.page_size}
                    currentPageItemCount={data_rows.length}
                    isLoading={loading}
                    onPageChange={pagination.on_page_change}
                    onPageSizeChange={pagination.on_page_size_change}
                />
            )}
        </>
    )
}

export default SimpleTable;